/**
 * SAB request/reply ring (ADR-0011 phase 1, version field per ADR-0032).
 *
 * Byte layout:
 * ```
 *   ┌────────────┬───────────┬───────────────────────────────────────────┐
 *   │  0 ..   4  │ VERSION   │ Int32; SyncRpc protocol version (ADR-0032)│
 *   │  4 ..   8  │ REQ_STATE │ Int32; 0=idle, 1=pending, 2=handling,     │
 *   │             │           │        3=writing                         │
 *   │  8 ..  12  │ REP_STATE │ Int32; 0 = no-reply, 1 = reply-ready      │
 *   │ 12 ..  16  │ REQ_LEN   │ Int32; request payload length in bytes    │
 *   │ 16 ..  20  │ REP_LEN   │ Int32; reply payload length in bytes      │
 *   │ 20 .. 20+C │ REQ_PAY   │ request payload (C = payloadCapacity)     │
 *   │20+C..20+2C │ REP_PAY   │ reply payload                             │
 *   └────────────┴───────────┴───────────────────────────────────────────┘
 * ```
 *
 * Wait-notify slots: REQ_STATE/REP_STATE. VERSION (ADR-0032) is stamped by
 * every `writeRequest`/`writeReply` and validated by every
 * `readRequest`/`consumeReply`; mismatched frames throw
 * {@link SyncRpcProtocolMismatchError} before payload is decoded.
 *
 * Single-request-at-a-time. Caller writes a request and blocks via
 * `waitReply`; responder reads via `readRequest` and answers via
 * `writeReply`. Multi-in-flight pipelining is out of scope per ADR-0011.
 */

import { SYNC_RPC_PROTOCOL_VERSION, SyncRpcProtocolMismatchError } from './sync-rpc.ts';

// `Atomics.waitAsync` ships in ES2024; lib target is ES2023, so we declare
// the narrow signature here rather than bump the workspace-wide lib option.
type WaitAsyncResult =
  | { async: false; value: 'ok' | 'not-equal' | 'timed-out' }
  | { async: true; value: Promise<'ok' | 'timed-out'> };
interface AtomicsWithWaitAsync {
  waitAsync(
    typedArray: Int32Array,
    index: number,
    value: number,
    timeout?: number,
  ): WaitAsyncResult;
}
function atomicsWaitAsync(ia: Int32Array, i: number, v: number, t: number): WaitAsyncResult {
  if (typeof (Atomics as unknown as AtomicsWithWaitAsync).waitAsync !== 'function') {
    throw new TypeError('Atomics.waitAsync is not a function');
  }
  return (Atomics as unknown as AtomicsWithWaitAsync).waitAsync(ia, i, v, t);
}

function waitDeadline(timeout: number): number {
  const duration = Number.isNaN(timeout) ? Number.POSITIVE_INFINITY : Math.max(0, timeout);
  return Number.isFinite(duration) ? performance.now() + duration : Number.POSITIVE_INFINITY;
}

export const SAB_RING_HEADER_BYTES = 20;
export const VERSION_OFFSET = 0;
export const REQ_STATE_OFFSET = 4;
export const REP_STATE_OFFSET = 8;
export const REQ_LEN_OFFSET = 12;
export const REP_LEN_OFFSET = 16;
export const DEFAULT_PAYLOAD_CAPACITY = 1024 * 1024; // 1 MiB

const STATE_IDLE = 0;
const STATE_READY = 1;
const STATE_HANDLING = 2;
const STATE_WRITING = 3;
type CallerPhase = 'idle' | 'live';
type ResponderPhase = 'idle' | 'handling' | 'reply-published';
export const VERSION_INDEX = VERSION_OFFSET >> 2;
const REQ_STATE_INDEX = REQ_STATE_OFFSET >> 2;
const REP_STATE_INDEX = REP_STATE_OFFSET >> 2;
const REQ_LEN_INDEX = REQ_LEN_OFFSET >> 2;
const REP_LEN_INDEX = REP_LEN_OFFSET >> 2;

/** Compile-time documentation of the header layout. */
export interface SabRingHeader {
  readonly VERSION: typeof VERSION_OFFSET;
  readonly REQ_STATE: typeof REQ_STATE_OFFSET;
  readonly REP_STATE: typeof REP_STATE_OFFSET;
  readonly REQ_LEN: typeof REQ_LEN_OFFSET;
  readonly REP_LEN: typeof REP_LEN_OFFSET;
  readonly HEADER_BYTES: typeof SAB_RING_HEADER_BYTES;
  readonly REQ_PAYLOAD_OFFSET: number;
  readonly REP_PAYLOAD_OFFSET: number;
  readonly PAYLOAD_CAPACITY: number;
}

/** Thrown by {@link SabRing.waitReply} when the timeout elapses. */
export class RingTimeoutError extends Error {
  readonly code = 'ERINGTIMEOUT' as const;
  constructor(timeoutMs: number, headerState?: string) {
    super(
      `SAB ring waitReply timed out after ${timeoutMs}ms${headerState === undefined ? '' : ` (${headerState})`}`,
    );
    this.name = 'RingTimeoutError';
  }
}

/** Thrown when a payload exceeds the ring's configured capacity. */
export class RingPayloadTooLargeError extends Error {
  readonly code = 'ERINGPAYLOAD' as const;
  constructor(
    readonly bytes: number,
    readonly capacity: number,
  ) {
    super(`SAB ring payload (${bytes} bytes) exceeds capacity (${capacity} bytes)`);
    this.name = 'RingPayloadTooLargeError';
  }
}

/** A claimed request has an invalid header and must receive an in-band error. */
export class RingCorruptRequestError extends Error {
  readonly code = 'ERINGCORRUPTREQUEST' as const;
  constructor(message: string) {
    super(message);
    this.name = 'RingCorruptRequestError';
  }
}

export interface CreateSabRingOptions {
  /** Per-direction payload capacity in bytes. Defaults to 1 MiB. */
  payloadCapacity?: number;
  /** Expected SyncRpc protocol version (ADR-0032). Defaults to
   * {@link SYNC_RPC_PROTOCOL_VERSION}; only tests should override. */
  expectedVersion?: number;
}

export interface CreateSabRingResult {
  readonly sab: SharedArrayBuffer;
  readonly ring: SabRing;
}

/** Allocates a new {@link SharedArrayBuffer} sized for a single ring and
 * returns the buffer (to transfer to another realm) plus a local
 * {@link SabRing}. Peers attach via {@link SabRing.attach}. */
export function createSabRing(opts: CreateSabRingOptions = {}): CreateSabRingResult {
  const payloadCapacity = opts.payloadCapacity ?? DEFAULT_PAYLOAD_CAPACITY;
  if (!Number.isInteger(payloadCapacity) || payloadCapacity <= 0) {
    throw new RangeError(
      `createSabRing: payloadCapacity must be a positive integer, got ${payloadCapacity}`,
    );
  }
  const totalBytes = SAB_RING_HEADER_BYTES + payloadCapacity * 2;
  const sab = new SharedArrayBuffer(totalBytes);
  return {
    sab,
    ring: SabRing.attach(sab, payloadCapacity, { expectedVersion: opts.expectedVersion }),
  };
}

/** Options accepted by {@link SabRing.attach}. */
export interface AttachSabRingOptions {
  /** Expected SyncRpc protocol version (ADR-0032). Defaults to
   * {@link SYNC_RPC_PROTOCOL_VERSION}. */
  expectedVersion?: number;
}

/** Wrapper around a {@link SharedArrayBuffer} implementing the
 * request/reply protocol. The same class is used on both peers — role is
 * determined by which methods you invoke. {@link writeRequest} +
 * {@link waitReply} are caller-side; {@link readRequest} +
 * {@link writeReply} are responder-side. */
export class SabRing {
  private readonly i32: Int32Array;
  private readonly bytes: Uint8Array;
  readonly payloadCapacity: number;
  readonly reqPayloadOffset: number;
  readonly repPayloadOffset: number;
  readonly header: SabRingHeader;
  /** Protocol version this peer expects on every incoming frame (ADR-0032). */
  readonly expectedVersion: number;
  /** Realm-local witness for the caller half of the shared exchange claim. */
  private callerPhase: CallerPhase = 'idle';
  /** Realm-local witness for the responder half of the shared exchange claim. */
  private responderPhase: ResponderPhase = 'idle';

  private constructor(sab: SharedArrayBuffer, payloadCapacity: number, expectedVersion: number) {
    const expected = SAB_RING_HEADER_BYTES + payloadCapacity * 2;
    // Exact match (ADR-0084 #19): the parent allocates exactly HEADER + 2×C, so
    // a capacity that disagrees with the buffer size means the two peers picked
    // different values — reject loudly rather than read the wrong REP/REQ slot.
    if (sab.byteLength !== expected) {
      throw new RangeError(
        `SabRing: SharedArrayBuffer is ${sab.byteLength} bytes; capacity ${payloadCapacity} requires exactly ${expected} (peers disagree on payloadCapacity)`,
      );
    }
    this.i32 = new Int32Array(sab, 0, SAB_RING_HEADER_BYTES >> 2);
    this.bytes = new Uint8Array(sab);
    this.payloadCapacity = payloadCapacity;
    this.reqPayloadOffset = SAB_RING_HEADER_BYTES;
    this.repPayloadOffset = SAB_RING_HEADER_BYTES + payloadCapacity;
    this.expectedVersion = expectedVersion;
    this.header = {
      VERSION: VERSION_OFFSET,
      REQ_STATE: REQ_STATE_OFFSET,
      REP_STATE: REP_STATE_OFFSET,
      REQ_LEN: REQ_LEN_OFFSET,
      REP_LEN: REP_LEN_OFFSET,
      HEADER_BYTES: SAB_RING_HEADER_BYTES,
      REQ_PAYLOAD_OFFSET: this.reqPayloadOffset,
      REP_PAYLOAD_OFFSET: this.repPayloadOffset,
      PAYLOAD_CAPACITY: payloadCapacity,
    };
  }

  /**
   * Forensic snapshot of the atomic header — every protocol-violation throw
   * carries it. Two CI flake signatures ("cannot writeRequest while a previous
   * reply is unread", an empty reply frame) surfaced with no state at all,
   * making the primal violation undiagnosable; the snapshot names it.
   */
  private headerState(): string {
    const version = Atomics.load(this.i32, VERSION_INDEX);
    const req = Atomics.load(this.i32, REQ_STATE_INDEX);
    const rep = Atomics.load(this.i32, REP_STATE_INDEX);
    const reqLen = Atomics.load(this.i32, REQ_LEN_INDEX);
    const repLen = Atomics.load(this.i32, REP_LEN_INDEX);
    return `header: version=${version} req=${stateName(req)} rep=${stateName(rep)} reqLen=${reqLen} repLen=${repLen}`;
  }

  /** Attaches a {@link SabRing} to an existing {@link SharedArrayBuffer}.
   * Both peers must agree on `payloadCapacity`. `expectedVersion`
   * (ADR-0032) defaults to {@link SYNC_RPC_PROTOCOL_VERSION}. */
  static attach(
    sab: SharedArrayBuffer,
    payloadCapacity: number = DEFAULT_PAYLOAD_CAPACITY,
    opts: AttachSabRingOptions = {},
  ): SabRing {
    const expectedVersion = opts.expectedVersion ?? SYNC_RPC_PROTOCOL_VERSION;
    return new SabRing(sab, payloadCapacity, expectedVersion);
  }

  /** Caller-side: copy `payload` into the request slot, stamp VERSION with
   * {@link expectedVersion} (ADR-0032), and wake the responder. Throws
   * {@link RingPayloadTooLargeError} on oversize; throws if a previous
   * reply/request is still unread. */
  writeRequest(payload: Uint8Array): void {
    this.writeRequestWithVersion(payload, this.expectedVersion);
  }

  /** Test/diagnostic hook: write a request with an explicit version
   * stamp. Production code uses {@link writeRequest}. (ADR-0032) */
  writeRequestWithVersion(payload: Uint8Array, version: number): void {
    if (payload.byteLength > this.payloadCapacity) {
      throw new RingPayloadTooLargeError(payload.byteLength, this.payloadCapacity);
    }
    if (Atomics.load(this.i32, REP_STATE_INDEX) !== STATE_IDLE) {
      throw new Error(
        `SabRing: cannot writeRequest while a previous reply is unread (${this.headerState()})`,
      );
    }
    if (this.callerPhase === 'live' && Atomics.load(this.i32, REQ_STATE_INDEX) === STATE_IDLE) {
      throw new Error(
        `SabRing: cannot writeRequest while this caller still owns a live request (${this.headerState()})`,
      );
    }
    const requestState = Atomics.compareExchange(
      this.i32,
      REQ_STATE_INDEX,
      STATE_IDLE,
      STATE_WRITING,
    );
    if (requestState !== STATE_IDLE) {
      throw new Error(
        `SabRing: cannot writeRequest while a previous request is ${
          requestState === STATE_HANDLING
            ? 'being handled'
            : requestState === STATE_WRITING
              ? 'being written'
              : 'unread'
        } (${this.headerState()})`,
      );
    }
    this.bytes.set(payload, this.reqPayloadOffset);
    Atomics.store(this.i32, REQ_LEN_INDEX, payload.byteLength);
    // VERSION stamped before STATE flip so the responder sees a consistent header.
    Atomics.store(this.i32, VERSION_INDEX, version);
    Atomics.store(this.i32, REQ_STATE_INDEX, STATE_READY);
    this.callerPhase = 'live';
    Atomics.notify(this.i32, REQ_STATE_INDEX);
  }

  /** Caller-side: sync block on reply via `Atomics.wait` (Worker realm
   * only). Throws {@link RingTimeoutError} on timeout,
   * {@link SyncRpcProtocolMismatchError} on version mismatch (ADR-0032). */
  waitReply(timeoutMs?: number): Uint8Array {
    const timeout = timeoutMs ?? Number.POSITIVE_INFINITY;
    const deadline = waitDeadline(timeout);
    for (;;) {
      this.assertCallerOwnsLiveRequest();
      if (Atomics.load(this.i32, REP_STATE_INDEX) !== STATE_IDLE) return this.consumeReply();
      const remaining = deadline - performance.now();
      if (remaining <= 0) throw new RingTimeoutError(timeout, this.headerState());
      const result = Atomics.wait(this.i32, REP_STATE_INDEX, STATE_IDLE, remaining);
      if (result === 'timed-out') throw new RingTimeoutError(timeout, this.headerState());
    }
  }

  /** Async sibling of {@link waitReply} — awaitable from any realm via
   * `Atomics.waitAsync`. Not part of the production caller API. */
  async waitReplyAsync(timeoutMs?: number): Promise<Uint8Array> {
    const timeout = timeoutMs ?? Number.POSITIVE_INFINITY;
    const deadline = waitDeadline(timeout);
    for (;;) {
      this.assertCallerOwnsLiveRequest();
      if (Atomics.load(this.i32, REP_STATE_INDEX) !== STATE_IDLE) return this.consumeReply();
      const remaining = deadline - performance.now();
      if (remaining <= 0) throw new RingTimeoutError(timeout, this.headerState());
      const pending = atomicsWaitAsync(this.i32, REP_STATE_INDEX, STATE_IDLE, remaining);
      const result = pending.async ? await pending.value : pending.value;
      if (result === 'timed-out') throw new RingTimeoutError(timeout, this.headerState());
    }
  }

  /** Responder-side arm on REQ_STATE (ADR-0084 #17). Waits on the current
   * stable phase: IDLE until publication, WRITING until READY, or HANDLING
   * until caller release. Returns the raw `{async, value}` so the dispatcher
   * can branch on the synchronous `'not-equal'` fast path itself. */
  armRequest(timeoutMs: number): WaitAsyncResult {
    const requestState = Atomics.load(this.i32, REQ_STATE_INDEX);
    return atomicsWaitAsync(
      this.i32,
      REQ_STATE_INDEX,
      requestState === STATE_HANDLING
        ? STATE_HANDLING
        : requestState === STATE_WRITING
          ? STATE_WRITING
          : STATE_IDLE,
      timeoutMs,
    );
  }

  private consumeReply(): Uint8Array {
    this.assertCallerOwnsLiveRequest();
    // Pre-clear snapshot — the forensic state a corrupt-length throw reports.
    const stateBefore = this.headerState();
    const replyState = Atomics.load(this.i32, REP_STATE_INDEX);
    if (replyState !== STATE_READY) {
      throw new Error(
        `SabRing: cannot consume reply unless reply is ready (${this.headerState()})`,
      );
    }
    // Snapshot VERSION first, then clear state; the throw must not wedge the ring (ADR-0032).
    const version = Atomics.load(this.i32, VERSION_INDEX);
    const len = Atomics.load(this.i32, REP_LEN_INDEX);
    Atomics.store(this.i32, REP_LEN_INDEX, 0);
    Atomics.store(this.i32, REP_STATE_INDEX, STATE_IDLE);
    const requestState = Atomics.compareExchange(
      this.i32,
      REQ_STATE_INDEX,
      STATE_HANDLING,
      STATE_IDLE,
    );
    this.callerPhase = 'idle';
    if (requestState !== STATE_HANDLING) {
      throw new Error(
        `SabRing: cannot consume reply unless request is handling; found ${stateName(requestState)} (${stateBefore})`,
      );
    }
    Atomics.notify(this.i32, REQ_STATE_INDEX);
    if (version !== this.expectedVersion) {
      throw new SyncRpcProtocolMismatchError(this.expectedVersion, version);
    }
    if (len < 0 || len > this.payloadCapacity) {
      throw new Error(
        `SabRing: corrupt reply length ${len} (capacity ${this.payloadCapacity}) (${stateBefore})`,
      );
    }
    // ADR-0084 #18: zero-copy live VIEW into the reply slot — no copy-out. State
    // is already flipped to IDLE above, so the consumer MUST decode synchronously
    // before issuing the next writeReply; production callers do (sync-client.ts).
    return this.bytes.subarray(this.repPayloadOffset, this.repPayloadOffset + len);
  }

  /** Responder-side: non-blocking. Returns a zero-copy live VIEW of the
   * request payload bytes (ADR-0084 #18 — valid only until the next slot
   * write; decode synchronously) when pending, or `null`. Atomically claims
   * REQ_STATE until the caller consumes the reply; another dispatcher cannot
   * consume the same request and another caller cannot overwrite it while the
   * exchange is live. Throws {@link SyncRpcProtocolMismatchError} on version
   * mismatch — the responder should reply via {@link writeReplyWithVersion}
   * echoing the caller's version (ADR-0032). */
  readRequest(): Uint8Array | null {
    const requestState = Atomics.compareExchange(
      this.i32,
      REQ_STATE_INDEX,
      STATE_READY,
      STATE_HANDLING,
    );
    if (requestState === STATE_IDLE) {
      if (this.responderPhase === 'handling') {
        throw new Error(
          `SabRing: responder lost its handling claim before writing a reply (${this.headerState()})`,
        );
      }
      this.responderPhase = 'idle';
      return null;
    }
    if (requestState === STATE_WRITING) {
      if (this.responderPhase === 'handling') {
        throw new Error(
          `SabRing: responder handling claim changed to writing (${this.headerState()})`,
        );
      }
      this.responderPhase = 'idle';
      return null;
    }
    if (requestState === STATE_HANDLING) {
      if (this.responderPhase !== 'idle') return null;
      throw new Error(
        `SabRing: cannot readRequest while a previous request is being handled (${this.headerState()})`,
      );
    }
    if (requestState !== STATE_READY) {
      throw new Error(`SabRing: corrupt request state ${requestState} (${this.headerState()})`);
    }
    if (this.responderPhase === 'handling') {
      throw new Error(
        `SabRing: responder acquired a second handling claim (${this.headerState()})`,
      );
    }
    this.responderPhase = 'handling';
    // Pre-clear snapshot — the forensic state a corrupt-length throw reports.
    const stateBefore = this.headerState();
    // Snapshot VERSION first. The handling claim stays live through reply write,
    // so a second caller cannot enter the gap between request read and reply.
    const version = Atomics.load(this.i32, VERSION_INDEX);
    const len = Atomics.load(this.i32, REQ_LEN_INDEX);
    Atomics.store(this.i32, REQ_LEN_INDEX, 0);
    if (version !== this.expectedVersion) {
      throw new SyncRpcProtocolMismatchError(this.expectedVersion, version);
    }
    if (len < 0 || len > this.payloadCapacity) {
      throw new RingCorruptRequestError(
        `SabRing: corrupt request length ${len} (capacity ${this.payloadCapacity}) (${stateBefore})`,
      );
    }
    // ADR-0084 #18: zero-copy live VIEW into the request slot (decode now).
    return this.bytes.subarray(this.reqPayloadOffset, this.reqPayloadOffset + len);
  }

  /** Responder-side: write reply bytes, stamp VERSION with
   * {@link expectedVersion} (ADR-0032), and notify the caller. */
  writeReply(payload: Uint8Array): void {
    this.writeReplyWithVersion(payload, this.expectedVersion);
  }

  /** Test/diagnostic hook + recovery path for version mismatch — the
   * dispatcher echoes the caller's version back inside an error reply so
   * the caller can still decode the failure (ADR-0032). */
  writeReplyWithVersion(payload: Uint8Array, version: number): void {
    if (payload.byteLength > this.payloadCapacity) {
      throw new RingPayloadTooLargeError(payload.byteLength, this.payloadCapacity);
    }
    if (Atomics.load(this.i32, REP_STATE_INDEX) !== STATE_IDLE) {
      throw new Error(
        `SabRing: cannot writeReply while a previous reply is unread (${this.headerState()})`,
      );
    }
    if (this.responderPhase !== 'handling') {
      throw new Error(`SabRing: dispatcher does not own the live request (${this.headerState()})`);
    }
    this.bytes.set(payload, this.repPayloadOffset);
    Atomics.store(this.i32, REP_LEN_INDEX, payload.byteLength);
    Atomics.store(this.i32, VERSION_INDEX, version);
    Atomics.store(this.i32, REP_STATE_INDEX, STATE_READY);
    this.responderPhase = 'reply-published';
    Atomics.notify(this.i32, REP_STATE_INDEX);
  }

  private assertCallerOwnsLiveRequest(): void {
    if (this.callerPhase === 'live') return;
    throw new Error(`SabRing: caller does not own the live request (${this.headerState()})`);
  }
}

function stateName(value: number): string {
  return value === STATE_IDLE
    ? 'idle'
    : value === STATE_READY
      ? 'ready'
      : value === STATE_HANDLING
        ? 'handling'
        : value === STATE_WRITING
          ? 'writing'
          : String(value);
}
